import { AlertReportDetailDto } from 'src/generated-sources/openapi/alert-service/alert';
import { AlertReportTag, RelationshipLevel } from '../models/wallet-alert-report.models';
import { TagType } from 'src/app/shared/components/tag/tag.component';
import { IndicatorCategoryDto } from 'src/generated-sources/openapi/configuration-service/indicator';
import {
  resolveIndicatorLabel,
  getRelationshipLevelSuffix,
} from '../models/wallet-alert-report.constants';

export function groupPartnerAlerts(partnerAlerts: AlertReportDetailDto[]): AlertReportDetailDto[] {
  const map = new Map<string, AlertReportDetailDto>();

  for (const alert of partnerAlerts) {
    const key = `${alert.indicatorId}:${alert.relationshipLevel}`;
    const existing = map.get(key);

    if (existing) {
      if (alert.category === IndicatorCategoryDto.Data) {
        existing.currentValue = mergeRegistrationDataJson(
          existing.currentValue,
          alert.currentValue,
        );
      } else {
        existing.currentValue = String(Number(existing.currentValue) + Number(alert.currentValue));
        existing.previousValue = String(
          Number(existing.previousValue ?? 0) + Number(alert.previousValue ?? 0),
        );
      }
    } else {
      map.set(key, { ...alert });
    }
  }

  return Array.from(map.values());
}

function mergeRegistrationDataJson(base: string, incoming: string): string {
  try {
    const baseObj: Record<string, boolean | null> = JSON.parse(base);
    const incomingObj: Record<string, boolean | null> = JSON.parse(incoming);
    const merged: Record<string, boolean | null> = { ...baseObj };
    for (const key of Object.keys(incomingObj)) {
      if (incomingObj[key] === true) {
        merged[key] = true;
      } else if (!(key in merged)) {
        merged[key] = incomingObj[key];
      }
    }
    return JSON.stringify(merged);
  } catch {
    return base;
  }
}

export function transformAlertData(data: AlertReportDetailDto[]): AlertReportTag[] {
  return data
    .filter((d) => {
      if (d.category !== IndicatorCategoryDto.Value) return true;
      const hasQty = data.some(
        (o) =>
          o.indicatorGroup === d.indicatorGroup && o.category === IndicatorCategoryDto.Quantity,
      );
      return !hasQty;
    })
    .map((d) => ({
      ...d,
      visible: true,
      measuredWidth: 0,
      text: formatAlertText(d),
      type: determineTagType(d),
    }));
}

function determineTagType(alertDetail: AlertReportDetailDto): TagType {
  if (alertDetail.category === IndicatorCategoryDto.Data) {
    return 'warning';
  }

  const isScore = alertDetail?.indicatorName?.toLowerCase().includes('score') ?? false;
  const isPreviousLetterOrNaN = alertDetail.previousValue != null && isNaN(Number(alertDetail.previousValue));

  if (isScore && isPreviousLetterOrNaN) {
    return 'info';
  }

  const noVariation = alertDetail.previousValue === alertDetail.currentValue;
  const previousValue = Number(alertDetail.previousValue) || 0;
  const currentValue = Number(alertDetail.currentValue) || 0;

  if (noVariation) return 'info';
  if (isScore) {
    return currentValue < previousValue ? 'danger' : 'success';
  }

  return currentValue > previousValue ? 'danger' : 'success';
}

function formatAlertText(alertDetail: AlertReportDetailDto): string {
  const suffix = getRelationshipLevelSuffix(alertDetail.relationshipLevel as RelationshipLevel | undefined);
  const groupLabel = resolveIndicatorLabel(alertDetail.indicatorId, alertDetail.indicatorGroup);

  if (alertDetail.category === IndicatorCategoryDto.Data) {
    return groupLabel + suffix;
  }

  const isScore = alertDetail?.indicatorName?.toLowerCase().includes('score') ?? false;
  const isPreviousLetterOrNaN = alertDetail.previousValue != null && isNaN(Number(alertDetail.previousValue));

  if (isScore && isPreviousLetterOrNaN) {
    return `${groupLabel}${suffix}: ${alertDetail.currentValue}`;
  }

  const diff = Number(alertDetail.currentValue) - Number(alertDetail.previousValue);
  let sign = '';
  if (diff > 0) {
    sign = '+';
  } else if (diff < 0) {
    sign = '-';
  }

  const absoluteValue = Math.abs(diff);

  if (alertDetail.category === IndicatorCategoryDto.Value && !isScore) {
    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

    return `${groupLabel}${suffix}: ${sign}${formatter.format(absoluteValue)}`;
  }

  return `${groupLabel}${suffix}: ${sign}${absoluteValue}`;
}
